import "./ExternalCheckoutPage.scss";

import { Alert, Loader, Text } from "@fluentui/react-northstar";
import { UserDTO } from "@teams/types/api";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router";

import { useDatadog } from "../../_shared/actions/useDatadog";
import { useHubspotChat } from "../../_shared/actions/useHubspotChat";
import { Congratulations } from "../../_shared/components/Checkout/Congratulations";
import { StripeAgileCheckoutPage } from "../../_shared/components/Checkout/StripeAgileCheckoutPage";
import { TeamsLoader } from "../../_shared/components/TeamsLoader";
import { isError, isSuccess } from "../../_shared/services/ChannelApi";
import { paymentsApi } from "../../_shared/services/PaymentsApi";
import { userApi } from "../../_shared/services/UserApi";
import { clientEventTracker } from "../../_shared/utils/clientTelemetry";
import * as cyberMonday from "../../_shared/utils/cyberMonday";
import {
  CheckoutPlan,
  getAgileStandupStandardPlan,
  getMSTeamsPlanLevelFrequencies,
} from "../../_shared/utils/paymentHelpers";
import { getPageContext } from "../../_shared/utils/teamsHelpers";
import { MSALPageHost } from "../MSALPageHost";
import { PageHeader } from "../PageHeader";
import { useMicrosoftAuth } from "../useMicrosoftAuth";
import { StripeCoreCheckoutPage } from "./StripeCoreCheckoutPage";

const ExternalCheckoutPageComponent: React.FC = () => {
  const { search } = useLocation();

  const urlParms = new URLSearchParams(search);
  const referrer = urlParms.get("referrer");
  const isTrial = urlParms.get("trial") === "true";
  const trackingSource = urlParms.get("track");

  const app = getPageContext();
  const { result } = useMicrosoftAuth(["openid", "profile", "email"]);
  const [user, setUser] = useState<UserDTO>();
  const [showSuccess, setShowSuccess] = useState<{
    trialEnd?: Date;
  }>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [authError, setAuthError] = useState<string>();
  const [plan, setPlan] = useState<CheckoutPlan>();
  const { loadHubspotChat } = useHubspotChat();
  const {
    initialize: dataDogInit,
    startSessionRecording,
    stopSessionRecording,
    log,
  } = useDatadog("teams-core-checkout", 100, 100);

  useEffect(() => {
    if (!result?.idToken) {
      return;
    }

    (async () => {
      const u = await userApi.getUserWithToken(result.idToken);
      if (isSuccess(u)) {
        setUser(u);
        dataDogInit(u.userId);
        startSessionRecording();
        return () => {
          stopSessionRecording();
        };
      } else {
        log({ logLevel: "error", message: "Failed to get user: " + u.message });
        setAuthError(u.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.idToken]);

  useEffect(() => {
    if (!result?.idToken || !user) {
      return;
    }

    (async () => {
      loadHubspotChat(result?.idToken);
      clientEventTracker.track("checkout viewed", user, {
        app,
        trackingSource,
        isTrial,
      });

      if (getPageContext() === "/agile") {
        setPlan(getAgileStandupStandardPlan());
      } else {
        setIsLoading(true);
        const planLevels = await paymentsApi.getPricing(result.idToken);
        setIsLoading(false);
        if (!isError(planLevels)) {
          setPlan({
            planLevels: planLevels,
            planType: "licensed",
            quantity: cyberMonday.initLicenses(),
            frequency: "yearly",
            frequencyOptions: getMSTeamsPlanLevelFrequencies(planLevels),
          });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadHubspotChat, user, app, result?.idToken]);

  let messageBody;

  if (showSuccess) {
    messageBody = (
      <Congratulations
        trialEnd={showSuccess.trialEnd}
        plan={getPageContext() === "/agile" ? "agile-standup" : "msteams-standard"}
        closeButtonText="Return to Teams"
        onClose={() => {
          if (referrer) {
            window.open(referrer, "_blank");
          } else {
            window.open("https://teams.microsoft.com", "_blank");
          }
        }}
      />
    );
  }

  if (authError) {
    messageBody = (
      <Alert content="Oops, something went wrong please try again or contact support" warning />
    );
  }

  if (!user || !result?.idToken) {
    return <Loader />;
  }

  if (messageBody) {
    return (
      <div className="external-checkout-page">
        <PageHeader
          helpLink={{
            url: "https://www.polly.ai/help/microsoft-teams/buying-polly",
            label: "What is this?",
          }}
        />
        <section className="external-checkout-page__container external-checkout-page__alert">
          {messageBody}
        </section>
      </div>
    );
  }

  return (
    <div className="external-checkout-page">
      <section className="external-checkout-page__container">
        <TeamsLoader isLoading={!result.idToken || !!isLoading} loadingMessage="Loading ...">
          {result.idToken && !isLoading && plan && (
            <>
              {getPageContext() === "/agile" ? (
                <StripeAgileCheckoutPage
                  user={user}
                  authToken={result.idToken}
                  plan={plan}
                  payerInfo={{
                    email: user.upn,
                    name: user.name,
                  }}
                  isTrial={isTrial}
                  onCompleted={(success, trialEnd) => {
                    if (success) {
                      setShowSuccess({ trialEnd });
                    }
                  }}
                />
              ) : (
                <StripeCoreCheckoutPage
                  pricingPlans={plan.planLevels!}
                  user={user}
                  isTrial={isTrial}
                  authToken={result.idToken}
                  onCheckoutComplete={(success, trialEnd) => {
                    if (success) {
                      setShowSuccess({ trialEnd });
                    }
                  }}
                />
              )}
            </>
          )}
        </TeamsLoader>
      </section>
    </div>
  );
};

export const ExternalCheckoutPage: React.FC = () => {
  return (
    <MSALPageHost
      title={
        <>
          Sign in to verify and <Text weight="bold"> upgrade your Polly account</Text>
        </>
      }
    >
      <ExternalCheckoutPageComponent />
    </MSALPageHost>
  );
};
